import dynamic from 'next/dynamic';
import Loading from '@/common/components/atom/loading';
import Text from '@/common/components/atom/text';
import AppBar from '@/common/components/layouts/appBar';
import classNames from '@/common/utils/classNames';
import { useOneApp } from '@/modules/dashboard/apis/one-app';
import { HamdastAuth } from '@/modules/hamdast/components/auth';
import { HamdastPayment } from '@/modules/hamdast/components/paymentModal';
import { HamdastDoctorServicePayment } from '@/modules/hamdast/components/doctorServicePayment';
import { Report } from '@/modules/hamdast/components/report';
import { HamdastWidget } from '@/modules/hamdast/components/widget';
import { HamdastSupport, HamdastSupportRef } from '@/modules/hamdast/components/support';
import { HamdastRasanActivate } from '@/modules/hamdast/components/smsActivate';
import { HamdastRasanCharge } from '@/modules/hamdast/components/smsCharge';
import { useLoginModalContext } from '@/modules/login/context/loginModal';
import { useUserInfoStore } from '@/modules/login/store/userInfo';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Button from '@/common/components/atom/button';
// Shown only before an app opens; appFrame is reachable from the shared layout, so static
// imports put the launcher's CSS/JS on every page.
const GlobalContextsProvider = dynamic(() => import('.plasmic/plasmic/launcher/PlasmicGlobalContextsProvider'));
const LauncherProfile = dynamic(() => import('.plasmic/LauncherProfile'));
import Logo from '@/common/components/atom/logo';
import { constructUrlWithQuery, replaceKeysInString } from '@/modules/hamdast/utils/appUrl';
import Permissions from '@/modules/hamdast/components/permissions';
import { HamdastSubscriptionPayment, HamdastSubscriptionPaymentRef } from './components/subscription-payment';
import { HamdastClose } from './components/close';
import { HamdastFlow } from './components/flow';
import Link from 'next/link';
import { HamdastInvite } from './components/invite';
import { getPageFromOneAppData } from '@/modules/hamdast/utils/getPageFromOneAppData';
import { getProfileDoctorUserId } from './stores/profileDoctorInfo';

export const AppFrame = ({
  appKey,
  params,
  queries,
  showBackButton,
  dontShowProfile,
  dontShowAppBar,
  dontShowNotification,
  onChangeWidget,
  reportOpenSignal,
  onReportSubmit,
  onHamdastClose,
  closeVardastOnHamdastClose,
  onRunFullscreenChange,
}: {
  appKey: string;
  params: string[];
  queries?: any;
  showBackButton?: boolean;
  dontShowProfile?: boolean;
  dontShowAppBar?: boolean;
  dontShowNotification?: boolean;
  onChangeWidget?: (action?: 'add' | 'remove' | 'update') => void;
  reportOpenSignal?: number;
  onReportSubmit?: () => void;
  onHamdastClose?: () => void;
  closeVardastOnHamdastClose?: boolean;
  /**
   * وقتی اپ واقعا در حال اجراست (نه پروفایل لانچر) و صفحه‌اش layout.fullscreen دارد
   * true می‌شود — والد (مودال/دراور) با آن حالت تقریبا تمام‌صفحهٔ دسکتاپ را فعال می‌کند.
   */
  onRunFullscreenChange?: (fullscreen: boolean) => void;
}) => {
  const router = useRouter();
  const getOneApp = useOneApp({ appKey: appKey, pageKey: params?.[0] as string });
  const app = useMemo(() => getOneApp.data?.data ?? {}, [getOneApp.data?.data]);
  const page = useMemo(() => {
    if (!app?.fragments) return {};

    return getPageFromOneAppData(app, params?.[0] as string, (params?.slice(1) as string[]) ?? []) ?? {};
  }, [app, params]);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const subscriptionPaymentRef = useRef<HamdastSubscriptionPaymentRef>(null);
  const supportRef = useRef<HamdastSupportRef>(null);
  const user = useUserInfoStore(state => state.info);
  const isLogin = useUserInfoStore(state => state.isLogin);
  const userPending = useUserInfoStore(state => state.pending);
  const { handleOpenLoginModal } = useLoginModalContext();
  const [showApp, setShowApp] = useState(dontShowProfile);
  const [showTranslation, setShowTranslation] = useState(false);
  const [showSupportButton, setShowSupportButton] = useState(false);

  const isEmbeddedModal = Boolean(dontShowAppBar && dontShowProfile && dontShowNotification);

  const embedSrc = useMemo(() => {
    const replaceParameters = page?.embed_src ? replaceKeysInString(page.embed_src, page.parameters ?? [], params?.slice(1) ?? []) : '';
    const doctorUserId = getProfileDoctorUserId();
    return page?.embed_src
      ? constructUrlWithQuery(replaceParameters, {
          ...queries,
          user_id: user.id,
          ...(doctorUserId ? { doctor_user_id: doctorUserId, provider_user_id: doctorUserId } : {}),
          hamdast_embedded: true,
        })
      : null;
  }, [page, params, queries, user.id]);

  const isMetadataLoading = !embedSrc && (getOneApp.isLoading || getOneApp.isFetching);
  const showIframe = page?.is_protected_route ? !!user.id : true;

  useEffect(() => {
    if (!userPending && !isLogin && page?.is_protected_route) {
      handleOpenLoginModal({
        state: true,
        closable: false,
        description: `برای نمایش ${page.name?.fa} لازم است وارد شوید.`,
      });
    }

    return () => {
      handleOpenLoginModal({
        state: false,
      });
    };
  }, [isLogin, userPending, page]);

  useEffect(() => {
    if (showTranslation) {
      setTimeout(() => {
        setShowApp(true);
        setShowTranslation(false);
      }, 3000);
    }
  }, [showTranslation]);

  useEffect(() => {
    const checkSupportLink = async () => {
      if (params?.[0] !== 'launcher' || !appKey || !isLogin) return;

      try {
        const response = await axios.get(`https://apigw.paziresh24.com/v1/hamdast/apps/${appKey}/support`, {
          withCredentials: true,
        });

        if (response.data?.id) {
          setShowSupportButton(true);
        }
      } catch (error) {
        setShowSupportButton(false);
      }
    };

    if (!userPending) {
      checkSupportLink();
    }
  }, [appKey, isLogin, userPending, params?.[0]]);

  useEffect(() => {
    if (params?.[0] != 'launcher') {
      setShowApp(true);
    }
  }, [params?.[0]]);

  /* پروفایل لانچر فول‌اسکرین نمی‌شود؛ فقط بعد از اجرا و اگر توسعه‌دهنده فعالش کرده باشد */
  const runFullscreen = Boolean(showApp && !showTranslation && page?.layout?.fullscreen);
  useEffect(() => {
    onRunFullscreenChange?.(runFullscreen);
  }, [runFullscreen, onRunFullscreenChange]);

  return (
    <div
      className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden"
      data-hamdast-run-fullscreen={runFullscreen ? 'true' : undefined}
    >
      {!dontShowAppBar && (
        <AppBar
          title={page?.name?.fa ?? ''}
          backButton={showBackButton}
          titleLoading={!page?.name}
          notification={!dontShowNotification}
          actionButton={
            showSupportButton ? (
              <Button
                size="sm"
                variant="secondary"
                className="rounded-full h-8 font-semibold text-xs"
                onClick={() => supportRef.current?.open()}
              >
                پشتیبانی ابزارک
              </Button>
            ) : (
              <Report app_key={appKey as string} page_key={page?.key ?? ''} openSignal={reportOpenSignal} onSubmit={onReportSubmit} />
            )
          }
        />
      )}
      {embedSrc && <Link rel="preconnect" href={embedSrc!} />}
      {embedSrc && <Link rel="dns-prefetch" href={embedSrc!} />}
      {onHamdastClose && <HamdastClose onClose={onHamdastClose} closeVardast={closeVardastOnHamdastClose} />}
      {app?.id && isEmbeddedModal && (
        <>
          <HamdastPayment app_key={appKey} app_name={app.display_name?.fa} icon={app?.icon} iframeRef={iframeRef} />
          <HamdastDoctorServicePayment app_key={appKey} app_name={app.display_name?.fa} icon={app?.icon} iframeRef={iframeRef} />
          <HamdastSubscriptionPayment
            ref={subscriptionPaymentRef}
            app_key={appKey}
            app_name={app.display_name?.fa}
            icon={app?.icon}
            iframeRef={iframeRef}
          />
          <HamdastRasanActivate app_name={app.display_name?.fa} iframeRef={iframeRef} />
          <HamdastRasanCharge iframeRef={iframeRef} />
          <HamdastAuth app_key={appKey} iframeRef={iframeRef} />
          <HamdastFlow iframeRef={iframeRef} />
        </>
      )}
      {app?.id && !isEmbeddedModal && (
        <>
          <HamdastPayment app_key={appKey} app_name={app.display_name?.fa} icon={app?.icon} iframeRef={iframeRef} />
          <HamdastDoctorServicePayment app_key={appKey} app_name={app.display_name?.fa} icon={app?.icon} iframeRef={iframeRef} />
          <HamdastSubscriptionPayment
            ref={subscriptionPaymentRef}
            app_key={appKey}
            app_name={app.display_name?.fa}
            icon={app?.icon}
            iframeRef={iframeRef}
          />
          <HamdastRasanActivate app_name={app.display_name?.fa} iframeRef={iframeRef} />
          <HamdastRasanCharge iframeRef={iframeRef} />
          <HamdastAuth app_key={appKey} iframeRef={iframeRef} />
          <HamdastWidget app_name={app.display_name?.fa} app_id={app?.id} iframeRef={iframeRef} onChangeWidget={onChangeWidget} />
          <HamdastSupport app_name={app.display_name?.fa} ref={supportRef} app_key={appKey} iframeRef={iframeRef} />
          <HamdastInvite app_key={appKey} />
          <Permissions onClose={() => router.push('/dashboard')} />
          <HamdastFlow iframeRef={iframeRef} />
          {showTranslation && (
            <div className="w-full flex-grow bg-white flex flex-col gap-5 justify-center items-center">
              <div className="flex items-center gap-5">
                <div className="bg-white rounded-xl shadow-card w-16 h-16 flex justify-center items-center">
                  <Logo type="compact" width={40} />
                </div>
                <Loading className="w-6 h-6 text-primary" />

                <div className="bg-white rounded-xl shadow-card w-16 h-16 flex justify-center items-center">
                  <img src={app?.icon} alt={app?.name?.fa || 'App icon'} className="w-10 h-10 min-w-10 min-h-10 rounded-xl object-cover" />
                </div>
              </div>
              <span className="text-sm">
                درحال انتقال از <span className="font-bold">پذیرش۲۴ به {app?.name?.fa}</span>
              </span>
            </div>
          )}
          {!showApp && app?.key && !showTranslation && (
            <div className="w-full flex-grow bg-[#f4f5f8] flex flex-col gap-5 pb-16 justify-center items-center overflow-x-auto">
              <GlobalContextsProvider>
                <LauncherProfile
                  appKey={app?.key}
                  onClick={() => setShowTranslation(true)}
                  onSubscribe={async planKey => {
                    const result = await subscriptionPaymentRef.current?.open(planKey);
                    if (result?.success) {
                      setShowTranslation(true);
                    }
                  }}
                  onSubscribeOnly={planKey => subscriptionPaymentRef.current?.open(planKey) ?? Promise.resolve({ success: false })}
                />
              </GlobalContextsProvider>
            </div>
          )}
        </>
      )}
      {(showApp || showTranslation) && (
        <div
          className={classNames('relative min-h-0 w-full flex-1 overflow-hidden', {
            '!opacity-0 invisible absolute -left-[9999px]': showTranslation,
          })}
        >
          {isMetadataLoading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white">
              <Loading />
              <Text fontSize="sm" className="text-slate-600">
                لطفا کمی صبر کنید...
              </Text>
            </div>
          )}
          {!isMetadataLoading && !embedSrc && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white px-4 text-center">
              <Text fontSize="sm" className="text-slate-600">
                خطا در بارگذاری اپلیکیشن
              </Text>
            </div>
          )}
          {showIframe && embedSrc && (
            <iframe
              key={embedSrc}
              ref={iframeRef}
              className="absolute inset-0 h-full w-full border-0 bg-white"
              loading="eager"
              title={page?.name?.fa || app?.display_name?.fa || 'Hamdast app'}
              src={embedSrc}
              allow="microphone; camera; fullscreen; clipboard-write;"
              sandbox="allow-forms allow-modals allow-downloads allow-orientation-lock allow-pointer-lock allow-popups allow-popups-to-escape-sandbox allow-presentation allow-same-origin allow-scripts allow-top-navigation allow-top-navigation-by-user-activation allow-top-navigation-to-custom-protocols allow-storage-access-by-user-activation"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default AppFrame;
